/**
 * AI Assistant — header scope switcher (G1).
 *
 * A chip ("All jobs ▾" / "<job title> ▾") that opens a small glass popover
 * listing "All jobs" plus the jobs from GET /api/copilot/status `scopes.jobs`.
 * With more than eight jobs a search field filters the list. Picking an entry
 * calls `onPick(scope, title)`; the dock pins the choice.
 *
 * Built on the Radix Popover primitive already in `@/Components/ui/popover`
 * (click to open, Esc / outside click to close) with a plain listbox inside so
 * it stays keyboard friendly and testable. No new dependencies.
 */
import React, { useMemo, useState } from 'react';
import { Briefcase, Building2, Check, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/Components/ui/popover';
import { cn } from '@/lib/utils';
import { COMPANY_LABEL, COMPANY_SCOPE, jobScope, scopeKey } from './scope';

export const SEARCH_THRESHOLD = 8;

/**
 * Build the switcher's entries from the status payload. Always contains
 * "All jobs"; when the backend sent no `scopes`, the current job (if any) is
 * the only other entry so the chip still reads correctly.
 */
export function buildScopeOptions(scopes, currentJob) {
  const company = scopes?.company || {};
  const options = [
    {
      key: 'company',
      scope: COMPANY_SCOPE,
      title: company.label || COMPANY_LABEL,
      meta: Number.isFinite(Number(company.job_count)) && company.job_count !== null && company.job_count !== undefined
        ? `${Number(company.job_count)} ${Number(company.job_count) === 1 ? 'job' : 'jobs'}`
        : null,
    },
  ];
  const seen = new Set();
  const jobs = Array.isArray(scopes?.jobs) ? scopes.jobs : [];
  jobs.forEach((j) => {
    const id = j?.job_id ?? j?.id;
    if (id === null || id === undefined || seen.has(String(id))) return;
    seen.add(String(id));
    const count = Number(j.applicant_count);
    options.push({
      key: `job:${id}`,
      scope: jobScope(id),
      title: j.title || j.job_title || `Job ${id}`,
      status: j.status || null,
      meta: Number.isFinite(count) ? `${count} ${count === 1 ? 'applicant' : 'applicants'}` : null,
    });
  });
  if (currentJob && currentJob.job_id !== null && currentJob.job_id !== undefined && !seen.has(String(currentJob.job_id))) {
    const count = Number(currentJob.applicant_count);
    options.splice(1, 0, {
      key: `job:${currentJob.job_id}`,
      scope: jobScope(currentJob.job_id),
      title: currentJob.title || currentJob.job_title || `Job ${currentJob.job_id}`,
      status: currentJob.status || null,
      meta: Number.isFinite(count) ? `${count} ${count === 1 ? 'applicant' : 'applicants'}` : null,
    });
  }
  return options;
}

export default function CopilotScopeSwitcher({ scope, scopes, currentJob, onPick, disabled = false, className }) {
  const [openState, setOpenState] = useState(false);
  const [query, setQuery] = useState('');
  const options = useMemo(() => buildScopeOptions(scopes, currentJob), [scopes, currentJob]);
  const activeKey = scopeKey(scope);
  const active = options.find((o) => o.key === activeKey) || null;
  const label = active?.title || (activeKey === 'company' ? COMPANY_LABEL : currentJob?.title || 'This job');
  const showSearch = options.length - 1 > SEARCH_THRESHOLD;
  const q = query.trim().toLowerCase();
  const visible = q ? options.filter((o) => o.key === 'company' || o.title.toLowerCase().includes(q)) : options;

  const pick = (option) => {
    setOpenState(false);
    setQuery('');
    if (onPick) onPick(option.scope, option.title);
  };

  return (
    <Popover
      open={openState}
      onOpenChange={(next) => {
        setOpenState(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn('cp-chip cp-chip-mint cp-scope-chip max-w-[200px] shrink-0', className)}
          aria-label={`Scope: ${label}. Change scope`}
          aria-haspopup="listbox"
          aria-expanded={openState}
          data-testid="copilot-scope-chip"
          data-scope={activeKey}
          disabled={disabled}
          title={label}
        >
          {activeKey === 'company' ? (
            <Building2 className="h-3 w-3 shrink-0" aria-hidden="true" />
          ) : (
            <Briefcase className="h-3 w-3 shrink-0" aria-hidden="true" />
          )}
          <span className="truncate">{label}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="cp-glass-strong cp-scope-menu w-[260px] p-1.5" data-testid="copilot-scope-menu">
        <div className="cp-label px-2 pb-1 pt-0.5">Ask about</div>
        {showSearch && (
          <label className="cp-composer mx-1 mb-1 flex h-8 items-center gap-1.5 rounded-full px-2.5" style={{ borderRadius: 9999 }}>
            <Search className="cp-faint h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search jobs"
              aria-label="Search jobs"
              autoFocus
              className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-[var(--cp-text-faint)]"
              style={{ color: 'var(--cp-text)' }}
            />
          </label>
        )}
        <ul role="listbox" aria-label="Scope" className="cp-scroll max-h-[260px] overflow-y-auto">
          {visible.map((option) => {
            const selected = option.key === activeKey;
            return (
              <li key={option.key} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-scope={option.key}
                  onClick={() => pick(option)}
                  className={cn(
                    'cp-focus flex min-h-[36px] w-full items-center gap-2 rounded-lg px-2 py-1 text-left transition-colors',
                    selected ? 'cp-mint-fill' : 'hover:bg-[hsl(var(--cp-mint)/0.08)]'
                  )}
                >
                  {option.key === 'company' ? (
                    <Building2 className="cp-text-brand h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  ) : (
                    <Briefcase className="cp-faint h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium leading-4">{option.title}</span>
                    {option.meta && <span className="cp-muted cp-num block truncate text-[11px] leading-4">{option.meta}</span>}
                  </span>
                  {selected && <Check className="h-3.5 w-3.5 shrink-0" style={{ color: 'hsl(var(--cp-ok))' }} aria-hidden="true" />}
                </button>
              </li>
            );
          })}
          {!visible.length && <li className="cp-muted px-2 py-3 text-center text-xs">No jobs match</li>}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
